import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
/**
 * Editorial Authoring V2 — EDITORIAL_STORY smart authoring (BUILD 3).
 *
 * Rendered by EditorialSpreadWorkspaceV2.jsx in place of the generic
 * workspace body when block.master_type === "EDITORIAL_STORY".
 * Self-contained, mirrors EditorialHeroV2.jsx's architecture closely
 * (same save-safety model, same media/crop bridge) but built around
 * the Editorial Story data/recognition contract instead of HERO's.
 *
 * No import from @/components/editorial/ (the legacy authoring UI).
 * Allowed imports only: @/lib/*, and this app's own
 * @/components/editorial-v2/* siblings (EditorialMediaPickerV2,
 * EditorialCropV2).
 *
 * RECOGNITION: deterministic only, via the existing
 * structureEditorialStory() in @/lib/editorialSimplePilot.mjs
 * (confirmed signature: structureEditorialStory(rawText) → { headline,
 * subtitle, lead, quote, paragraphs: string[] }; the parser has no
 * eyebrow output — eyebrow is manual-only in this authoring flow, see
 * below). No provider call. Provider calls = 0, AI cost = $0. The
 * parser's algorithm itself is not touched in this BUILD.
 *
 * ── HERO LEFTOVER HANDOFF (consumption side) ────────────────────────
 * handoffSource (optional prop) is the HERO leftover text the page
 * (EditorialV2.jsx) decided this Story block should receive — or null
 * if this block isn't a handoff target right now. On receiving a
 * non-empty handoffSource:
 *   - if this block's own current source already equals the leftover
 *     (byte-for-byte after trim) → nothing to do, no banner.
 *   - if this block is truly empty — no raw source AND no existing
 *     structured/media content (see hasExistingStoryContent below,
 *     Owner QA fix) → auto-apply: the draft sourceText becomes the
 *     leftover, recognized resets to null (new source needs
 *     recognition), and a calm note "Текст продолжен из обложки" is
 *     shown. No backend write yet.
 *   - otherwise (there's raw source text, OR there's no raw source but
 *     the block already has a saved headline/paragraphs/photo etc.) →
 *     a compact inline banner ("В редакционной истории уже есть
 *     текст.") offers two explicit choices — "Открыть существующий"
 *     (leave the local draft as-is, no DB mutation) or "Заменить текст
 *     продолжением из обложки" (replace the local text draft only —
 *     existing media is left attached, nothing written to the backend
 *     until the author presses "Сохранить изменения"). No browser
 *     confirm()/alert() is used anywhere.
 * The transient handoff itself is owned and cleared by EditorialV2.jsx
 * once this block is actually saved — this component only reacts to
 * the prop, it never mutates or clears the parent's handoff state.
 *
 * ── SAVE SAFETY (same model as HERO BUILD 2's final correction) ─────
 * If sourceText is non-empty and recognized is still null (a new or
 * changed source that hasn't been run through "Распознать текст"
 * yet), Save is disabled with a calm caption — never a silent
 * overwrite of previously-saved structured fields. When recognized IS
 * null but sourceText is empty (a legitimate media-only story), Save
 * remains available and simply omits the structured fields from the
 * patch, leaving whatever was already saved untouched via the `...c`
 * spread.
 *
 * ── MEDIA / CROP BRIDGE (identical contract to HERO BUILD 2) ────────
 * One primary photo for Desktop + Mobile, reusing content.hero_url /
 * content.hero_media_id / content.media_ids and settings.focal_x/
 * focal_y/hero_zoom (desktop) + settings.mobile_focal_x/
 * mobile_focal_y/hero_mobile_zoom (mobile). On Save, hero_media_id +
 * hero_url + media_ids are synchronized together and any legacy
 * hero_mobile_media_id/hero_mobile_url override is cleared (mobile
 * crop settings are kept). EditorialMediaPickerV2 and EditorialCropV2
 * are reused as-is — no new picker, no new crop component.
 *
 * ── READER-FAITHFUL PREVIEW (final correction) ───────────────────────
 * Per the confirmed current EDITORIAL_STORY Reader contract:
 * settings.variant || "A" drives Desktop composition (A: photo left /
 * text right, B: text left / photo right — no variant C for Story),
 * text column order eyebrow → headline → subtitle → lead → paragraphs
 * → quote, using the same palette/background token family as HERO's
 * preview (duplicated locally here, not imported, to keep this file
 * self-contained per the BUILD's file scope). Mobile follows the
 * confirmed current EditorialStoryRender order exactly: eyebrow →
 * headline → subtitle → lead → photo → paragraphs → quote (photo sits
 * in the middle of the text flow, not before it). The modal preview
 * shows at most the first 3 paragraphs with a soft fade +
 * "текст продолжается" indicator when there are more — a viewport
 * limitation only; the full paragraphs array is always what gets
 * saved and read back.
 *
 * The crop frame itself is a fixed 4:5 for both Desktop and Mobile
 * (STORY_DESKTOP_ASPECT / STORY_MOBILE_ASPECT below) — confirmed as
 * the current Story authoring frame and the Reader Mobile explicit-
 * crop frame. editorialMediaFrameAspect/Label
 * (@/lib/editorialMediaFrame.mjs) only has a contract for HERO and
 * returns null for "EDITORIAL_STORY", so it is intentionally NOT
 * called here — editorialMediaFrame.mjs itself is not touched in this
 * BUILD.
 *
 * EXISTING FIELDS REUSED (no new backend fields, no migration):
 *   content.body, content.eyebrow, content.headline, content.subtitle,
 *   content.lead, content.quote, content.extra.editorial_story_source_text,
 *   content.extra.paragraphs, content.media_ids, content.hero_media_id,
 *   content.hero_url, content.hero_mobile_media_id (cleared on save),
 *   content.hero_mobile_url (cleared on save), settings.variant,
 *   settings.palette, settings.background, settings.focal_x,
 *   settings.focal_y, settings.hero_zoom, settings.mobile_focal_x,
 *   settings.mobile_focal_y, settings.hero_mobile_zoom
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { structureEditorialStory } from "@/lib/editorialSimplePilot.mjs";
import { mediaRecordUrl } from "@/lib/editorialMedia.mjs";
import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";
import { initialStoryImage, saveStoryContent } from "@/lib/editorialStoryMediaBindingV2.mjs";
import { MediaLibraryPickerV2, MEDIA_LIST, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";
import { PublicationDestinationV2 } from "./PublicationDestinationV2";
import { EDITORIAL_MASTER_SHOWCASE_ID, saveFirstEditorialStoryFromMaster } from "../../lib/editorialAuthorStartV1.mjs";
import { applyStoryParagraphFormat } from "../../lib/editorialStoryFormattingV1.mjs";
import { EditorialStoryFormattedParagraphV1 } from "./EditorialStoryFormattedParagraphV1";

// editorialMediaFrameAspect/Label (@/lib/editorialMediaFrame.mjs) only
// has a frame contract for HERO — calling it with "EDITORIAL_STORY"
// returns null. The confirmed current Story frame is 4:5 both for the
// existing authoring UI's image frame and for Reader Mobile's explicit
// crop, so it's hardcoded here as V2-local constants instead of
// touching editorialMediaFrame.mjs to add a new MASTER contract (out
// of scope for this BUILD).
const STORY_DESKTOP_ASPECT = 4 / 5;
const STORY_MOBILE_ASPECT = 4 / 5;
const STORY_DESKTOP_FRAME_LABEL = "Кадр публикации";
const STORY_MOBILE_FRAME_LABEL = "Кадр для телефона";

// A short list of common short Russian one/two-letter prepositions and
// conjunctions that should not be left hanging alone at a line end.
// Presentational only — applied to the local preview's text, never to
// what gets saved.
const NBSP_WORDS = ["и", "в", "к", "с", "у", "о", "а", "на", "до", "из", "от", "по", "за", "но", "что", "как", "не"];
function typographicNbsp(text) {
  if (!text) return text;
  const pattern = new RegExp(`(^|\\s)(${NBSP_WORDS.join("|")})\\s+`, "giu");
  return text.replace(pattern, (m, pre, word) => `${pre}${word}\u00A0`);
}

function emptyRecognized() {
  return { eyebrow: "", headline: "", subtitle: "", lead: "", quote: "", paragraphs: [] };
}

// Raw source + "already recognized?" derivation for EDITORIAL_STORY —
// same safety principle as HERO's deriveInitialHeroText: content.body
// (freshest shell input) takes priority over the stored
// editorial_story_source_text, and the block is only treated as
// already-structured when the current source is empty or matches the
// stored source exactly. Any new/changed body resets recognized to
// null so "Распознать текст" is shown for what was actually typed.
function deriveInitialStoryText(content) {
  const c = content || {};
  const bodySource = String(c.body || "");
  const storedSource = String(c.extra?.editorial_story_source_text || "");
  const initialSource = bodySource.trim() ? bodySource : storedSource;

  const hasStructuredFields = Boolean(
    c.eyebrow || c.headline || c.subtitle || c.lead || c.quote
    || (Array.isArray(c.extra?.paragraphs) && c.extra.paragraphs.length)
  );
  const isNewOrChangedSource = Boolean(bodySource.trim()) && bodySource.trim() !== storedSource.trim();

  if (!isNewOrChangedSource && hasStructuredFields) {
    return {
      sourceText: initialSource,
      recognized: {
        eyebrow: c.eyebrow || "",
        headline: c.headline || "",
        subtitle: c.subtitle || "",
        lead: c.lead || "",
        quote: c.quote || "",
        paragraphs: Array.isArray(c.extra?.paragraphs) ? [...c.extra.paragraphs] : [],
      },
    };
  }

  return { sourceText: initialSource, recognized: null };
}

// V2 shell → media bridge. Identical logic to HERO's
// deriveInitialHeroMedia (see EditorialHeroV2.jsx) — duplicated here
// rather than imported, to keep this file self-contained per BUILD 3's
// file scope (only EditorialCropV2/EditorialMediaPickerV2 are shared).
function deriveInitialStoryMedia(content) {
  return initialStoryImage(content);
}

// Owner QA fix: a Story can have no raw source (content.body /
// content.extra.editorial_story_source_text both empty) while already
// carrying real authored content — structured fields, paragraphs, or
// an attached photo. That block is NOT an empty handoff target and
// must never be silently auto-prefilled with a HERO leftover.
function hasExistingStoryContent(content) {
  const c = content || {};
  if (c.eyebrow || c.headline || c.subtitle || c.lead || c.quote) return true;
  if (Array.isArray(c.extra?.paragraphs) && c.extra.paragraphs.some((p) => String(p || "").trim())) return true;
  if (Array.isArray(c.media_ids) && c.media_ids.length) return true;
  if (c.hero_media_id) return true;
  if (c.hero_url) return true;
  return false;
}

// ── Reader design tokens (V2-only mirror, not an import) ────────────
// Same token family as EditorialHeroV2.jsx's preview, duplicated here
// for the same self-containment reason. Story's confirmed defaults
// differ from HERO's: the real EditorialStoryMaster fallback is
// `paletteByKey(s.palette) || HERO_PALETTES[2]` (index 2 = olive), and
// its background fallback resolves a "transparent" css value (which is
// what "full-image" maps to) to warm ivory (#EFE6D5) rather than
// leaving it transparent — see paletteColor()/backgroundColor() below.
const PALETTES = {
  ivory: "#EFE6D5",
  almond: "#D6B98A",
  olive: "#7E8A3E",
  cocoa: "#5B3B25",
  burgundy: "#7A2A2A",
  gold: "#C9A84C",
  pistachio: "#A6C088",
  apricot: "#E8B48A",
};
const BACKGROUNDS = {
  "warm-ivory": "#EFE6D5",
  "mineral-plaster": "#E6DFCF",
  "almond-paper": "#F1E7D2",
  "deep-cocoa": "#2A1810",
  "full-image": "transparent",
};
const TEXT_DARK = "#3B241A";
const TEXT_LIGHT = "#F1E4CB";

function paletteColor(key) { return PALETTES[key] || PALETTES.olive; }
function backgroundColor(key) {
  const value = BACKGROUNDS[key] || BACKGROUNDS["warm-ivory"];
  return value === "transparent" ? "#EFE6D5" : value;
}
function textColorFor(backgroundKey) { return backgroundKey === "deep-cocoa" ? TEXT_LIGHT : TEXT_DARK; }

function StoryPreviewPhoto({ mediaUrl, crop, aspect, rounded = true }) {
  return (
    <div className={`relative overflow-hidden bg-espresso/10 ${rounded ? "rounded-xl" : ""}`} style={{ aspectRatio: aspect }}>
      {mediaUrl && (
        <img src={mediaUrl} alt="" className="absolute inset-0 h-full w-full"
          style={{
            objectFit: "cover",
            objectPosition: `${crop.focal_x * 100}% ${crop.focal_y * 100}%`,
            transform: `scale(${crop.zoom})`,
            transformOrigin: `${crop.focal_x * 100}% ${crop.focal_y * 100}%`,
          }} />
      )}
    </div>
  );
}

function StoryTextPreview({ recognized, textColor, accentColor, backgroundHex }) {
  const paragraphs = recognized?.paragraphs || [];
  const shown = paragraphs.slice(0, 3);
  const hasMore = paragraphs.length > 3;

  return (
    <div>
      {recognized?.eyebrow && (
        <div className="text-[10px] uppercase tracking-widest opacity-60" style={{ color: textColor }}>
          {recognized.eyebrow}
        </div>
      )}
      {recognized?.headline && (
        <div className="mt-1 font-serif text-xl leading-snug" style={{ color: textColor }}>
          {typographicNbsp(recognized.headline)}
        </div>
      )}
      {accentColor && (recognized?.headline || recognized?.subtitle) && (
        <div className="mt-2 h-[2px] w-10" style={{ backgroundColor: accentColor }} />
      )}
      {recognized?.subtitle && (
        <div className="mt-2 font-serif text-sm opacity-90" style={{ color: textColor }}>
          {typographicNbsp(recognized.subtitle)}
        </div>
      )}
      {recognized?.lead && (
        <div className="mt-2 text-xs opacity-80" style={{ color: textColor }}>
          {typographicNbsp(recognized.lead)}
        </div>
      )}
      {shown.length > 0 && (
        <div className="mt-3 space-y-2">
          {shown.map((p, i) => (
            <EditorialStoryFormattedParagraphV1 key={i} text={p} preview
              className="text-xs leading-relaxed opacity-75" style={{ color: textColor }} />
          ))}
          {hasMore && (
            <div className="relative">
              <div className="h-6" style={{
                background: `linear-gradient(to bottom, transparent, ${backgroundHex})`,
              }} />
              <div className="text-[10px] italic opacity-60" style={{ color: textColor }}>
                Текст продолжается…
              </div>
            </div>
          )}
        </div>
      )}
      {recognized?.quote && (
        <div className="mt-3 border-l-2 pl-3 font-serif text-sm italic opacity-90"
          style={{ color: textColor, borderColor: accentColor || textColor }}>
          {typographicNbsp(recognized.quote)}
        </div>
      )}
    </div>
  );
}

export function EditorialStoryV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput, handoffSource, sourceMaterial, newStoryMode = false, onStartNewStory, onAuthorCreated }) {
  const mediaAccessZone = volume?.product_id ? "paid" : "public";
  const isShowcase = block.material_id === EDITORIAL_MASTER_SHOWCASE_ID;
  const isFirstSave = isShowcase && newStoryMode;
  // Local blank workspace only. Never copy QA text, photo or presentation settings.
  const c = isFirstSave ? {} : (block.content || {});
  const s = isFirstSave ? {} : (block.settings || {});

  const initialText = deriveInitialStoryText(c);
  const initialMedia = deriveInitialStoryMedia(c);

  const [sourceText, setSourceText] = useState(initialText.sourceText);
  const [recognized, setRecognized] = useState(initialText.recognized);
  const [activeParagraphIndex, setActiveParagraphIndex] = useState(null);
  const lastSelectionRef = useRef(null);
  const paragraphInputsRef = useRef({});
  const [editingFields, setEditingFields] = useState(false);
  // QA FIX: lets a recognized Story reopen its raw source without
  // losing the recognized result just by opening it. sourceEditing
  // only controls textarea visibility; recognizedSourceText is a
  // transient snapshot of the source that produced the current
  // recognized result, compared against sourceText to decide whether
  // an actual change happened (not persisted anywhere).
  const [sourceEditing, setSourceEditing] = useState(false);
  const [recognizedSourceText, setRecognizedSourceText] = useState(
    initialText.recognized ? initialText.sourceText : ""
  );

  const [handoffNote, setHandoffNote] = useState(false);
  const [handoffConflict, setHandoffConflict] = useState(null); // the offered leftover text, or null

  const [mediaId, setMediaId] = useState(initialMedia.mediaId);
  const [mediaUrl, setMediaUrl] = useState(initialMedia.mediaUrl);
  // Display-only transport: keep the original URL/ID in the author draft and Save.
  // Protected GridFS images require Axios Bearer + ephemeral blob URL for <img>.
  const displayMediaUrl = useEditorialMediaUrl(mediaUrl);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [desktopCrop, setDesktopCrop] = useState({
    focal_x: s.focal_x ?? 0.5, focal_y: s.focal_y ?? 0.5, zoom: s.hero_zoom ?? 1,
  });
  const [mobileCrop, setMobileCrop] = useState({
    focal_x: s.mobile_focal_x ?? s.focal_x ?? 0.5,
    focal_y: s.mobile_focal_y ?? s.focal_y ?? 0.5,
    zoom: s.hero_mobile_zoom ?? s.hero_zoom ?? 1,
  });
  const [cropOpen, setCropOpen] = useState(null); // null | "desktop" | "mobile"

  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  const saveInFlight = useRef(false);

  // Reset all local state when the open block actually changes (the
  // workspace component instance can be reused across different
  // blocks of the same MASTER in principle, mirroring the same guard
  // already present in EditorialHeroV2.jsx).
  useEffect(() => {
    const nextContent = isFirstSave ? {} : (block.content || {});
    const nextText = deriveInitialStoryText(nextContent);
    const nextMedia = deriveInitialStoryMedia(nextContent);
    const nextSettings = isFirstSave ? {} : (block.settings || {});

    setSourceText(nextText.sourceText);
    setRecognized(nextText.recognized);
    setEditingFields(false);
    setSourceEditing(false);
    setRecognizedSourceText(nextText.recognized ? nextText.sourceText : "");
    setHandoffNote(false);
    setHandoffConflict(null);

    setMediaId(nextMedia.mediaId);
    setMediaUrl(nextMedia.mediaUrl);

    setDesktopCrop({
      focal_x: nextSettings.focal_x ?? 0.5, focal_y: nextSettings.focal_y ?? 0.5, zoom: nextSettings.hero_zoom ?? 1,
    });
    setMobileCrop({
      focal_x: nextSettings.mobile_focal_x ?? nextSettings.focal_x ?? 0.5,
      focal_y: nextSettings.mobile_focal_y ?? nextSettings.focal_y ?? 0.5,
      zoom: nextSettings.hero_mobile_zoom ?? nextSettings.hero_zoom ?? 1,
    });
    setSaveState("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, isFirstSave]);

  // HERO leftover handoff consumption — see file header for the full
  // contract. Runs once per (block.id, handoffSource) pairing; reads
  // sourceText via closure at that moment (i.e. this block's own
  // freshly-derived source), not on every keystroke.
  useEffect(() => {
    const leftover = String(handoffSource || "");
    if (!leftover.trim()) return;

    const ownSource = sourceText.trim();

    if (ownSource === leftover.trim()) {
      return; // already matches — nothing to do, no banner
    }

    // Owner QA fix: "empty" means no raw source AND no existing
    // structured/media content — a Story with a saved headline,
    // paragraphs, or a photo is never treated as empty just because
    // content.body/editorial_story_source_text happen to be blank.
    const hasExisting = hasExistingStoryContent(block.content || {});
    if (!ownSource && !hasExisting) {
      setSourceText(leftover);
      setRecognized(null);
      setHandoffNote(true);
      setHandoffConflict(null);
      return;
    }

    setHandoffConflict(leftover);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffSource, block.id]);

  // Resolve a media_ids-derived id that has no already-known URL.
  useEffect(() => {
    if (!mediaId || mediaUrl) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(MEDIA_LIST);
        if (cancelled) return;
        const records = Array.isArray(data) ? data : (data?.items || data?.results || []);
        const record = records.find((r) => r.id === mediaId);
        const url = record ? mediaRecordUrl(record) : null;
        if (url) setMediaUrl(url);
      } catch {
        // Non-fatal: thumbnail/preview stays empty until the author picks again.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, mediaId]);  const storyVariant = s.variant || "A";
  const storyPalette = s.palette || "olive";
  const storyBackground = s.background || "warm-ivory";
  const desktopAspect = STORY_DESKTOP_ASPECT;
  const mobileAspect = STORY_MOBILE_ASPECT;
  const desktopFrameLabel = STORY_DESKTOP_FRAME_LABEL;
  const mobileFrameLabel = STORY_MOBILE_FRAME_LABEL;

  const sourceNeedsRecognition = Boolean(sourceText.trim()) && !recognized;

  const recognize = () => {
    const result = structureEditorialStory(sourceText);
    setRecognized({
      // The parser has no eyebrow output — eyebrow is manual-only.
      // Carry forward whatever the author already has (either from a
      // prior local edit in this session, or the block's saved value)
      // rather than resetting it on every re-recognition.
      eyebrow: recognized?.eyebrow || c.eyebrow || "",
      headline: result.headline,
      subtitle: result.subtitle,
      lead: result.lead,
      quote: result.quote,
      paragraphs: result.paragraphs,
    });
    setRecognizedSourceText(sourceText);
    setEditingFields(false);
    setSourceEditing(false);
  };

  // QA FIX: typing in the reopened source textarea only invalidates
  // the current recognized Story structure once the text actually
  // differs from the source that produced it — merely opening
  // "Изменить исходный текст" must not blank anything.
  const handleSourceTextChange = (value) => {
    setSourceText(value);
    if (recognized && value.trim() !== recognizedSourceText.trim()) {
      setRecognized(null);
    }
  };

  const returnToRecognized = () => {
    setSourceEditing(false);
  };

  const updateField = (field, value) => {
    setRecognized((r) => ({ ...(r || emptyRecognized()), [field]: value }));
  };

  const updateParagraph = (index, value) => {
    setRecognized((r) => {
      const paragraphs = [...(r?.paragraphs || [])];
      paragraphs[index] = value;
      return { ...(r || emptyRecognized()), paragraphs };
    });
  };

  const formatActiveParagraph = (kind, index) => {
    if (!recognized?.paragraphs?.length || index < 0 || index >= recognized.paragraphs.length) return;
    const input = paragraphInputsRef.current[index];
    const previous = recognized.paragraphs[index];
    const stored = lastSelectionRef.current;
    const selection = stored?.index === index && stored?.text === previous ? stored : null;
    const start = selection?.start ?? input?.selectionStart ?? 0;
    const end = selection?.end ?? input?.selectionEnd ?? start;
    if ((kind === "bold" || kind === "italic") && start === end) {
      toast.info("Сначала выделите нужные слова внутри абзаца.");
      input?.focus();
      return;
    }
    const next = applyStoryParagraphFormat(previous, kind, start, end);
    if (next === previous) return;
    updateParagraph(index, next);
    lastSelectionRef.current = null;
    requestAnimationFrame(() => {
      const target = paragraphInputsRef.current[index];
      if (!target) return;
      target.focus();
      const offset = kind === "bold" ? 4 : kind === "italic" ? 2 : 0;
      const cursor = Math.min(next.length, end + offset);
      target.setSelectionRange(cursor, cursor);
    });
  };

  const removeParagraph = (index) => {
    setRecognized((r) => ({
      ...(r || emptyRecognized()),
      paragraphs: (r?.paragraphs || []).filter((_, i) => i !== index),
    }));
  };

  const addParagraph = () => {
    setRecognized((r) => ({ ...(r || emptyRecognized()), paragraphs: [...(r?.paragraphs || []), ""] }));
  };

  const applyHandoffReplace = () => {
    setSourceText(handoffConflict);
    setRecognized(null);
    setSourceEditing(false);
    setRecognizedSourceText("");
    setHandoffConflict(null);
    setHandoffNote(true);
    // Existing photo (mediaId/mediaUrl) is intentionally left as-is —
    // the handoff replaces the text draft only, never removes media.
  };
  const dismissHandoffConflict = () => {
    setHandoffConflict(null);
  };

  const pickFromLibrary = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    setMediaId(media.id);
    setMediaUrl(url);
    setLibraryOpen(false);
  };

  const removePhoto = () => {
    setMediaId(null);
    setMediaUrl(null);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("access_zone", mediaAccessZone);
      const { data } = await api.post(MEDIA_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      setMediaId(data.id);
      setMediaUrl(url);
      toast.success("Фото загружено");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const saveCrop = (device, values) => {
    if (device === "desktop") setDesktopCrop(values);
    else setMobileCrop(values);
    setCropOpen(null);
  };

  const save = async () => {
    if (sourceNeedsRecognition || saveInFlight.current) return;
    if (isShowcase && !isFirstSave) {
      toast.info("Это образец оформления. Сначала начни свою статью.");
      return;
    }
    if (isFirstSave && !sourceText.trim()) {
      toast.info("Добавь собственный текст статьи.");
      return;
    }

    saveInFlight.current = true;
    setSaveState("saving");
    try {
      const nextContent = saveStoryContent({
        original: c, sourceText, recognized, mediaId, mediaUrl,
      });
      const nextSettings = {
        ...s,
        focal_x: desktopCrop.focal_x,
        focal_y: desktopCrop.focal_y,
        hero_zoom: desktopCrop.zoom,
        mobile_focal_x: mobileCrop.focal_x,
        mobile_focal_y: mobileCrop.focal_y,
        hero_mobile_zoom: mobileCrop.zoom,
      };
      if (isFirstSave) {
        const created = await saveFirstEditorialStoryFromMaster({
          api, sourceMaterial, sourceBlock: block,
          title: String(recognized?.headline || "").trim(),
          content: nextContent, settings: nextSettings,
        });
        setSaveState("saved");
        toast.success("Авторская статья сохранена");
        onAuthorCreated?.(created);
        return;
      }
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent, settings: nextSettings });
      setSaveState("saved");
      const savedMediaItems = mediaId && mediaUrl ? [{ id: mediaId, url: mediaUrl }] : [];
      onSaved?.({ ...block, content: nextContent, settings: nextSettings }, savedMediaItems);
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    } finally {
      saveInFlight.current = false;
    }
  };

  const saveLabel = {
    idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения",
  }[saveState];

  // Once a recognized result exists (fresh recognition, including a
  // prose-only source with no real headline), it is authoritative for
  // the workspace title — it must not fall back to a stale, previously
  // persisted c.headline. c.headline is only shown before any
  // recognition has happened in this session.
  const title = recognized
    ? (recognized.headline || "Редакционная история")
    : (c.headline || "Редакционная история");
  const paragraphCount = (recognized?.paragraphs || []).length;

  return (
    <div
      onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-v2-story-workspace"
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between gap-3 border-b border-espresso/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Редакционная история</div>
            <div className="mt-0.5 truncate font-serif text-xl text-espresso">{title}</div>
          </div>
          {saveLabel && (
            <span className="shrink-0 pt-1 text-[11px] uppercase tracking-widest text-espresso/50"
              data-testid="editorial-v2-story-save-state">
              {saveLabel}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {!isShowcase && <PublicationDestinationV2 materialId={block.material_id} volume={volume} />}
          {isShowcase && (
            <div className="rounded-xl border border-espresso/15 bg-white/65 p-3 text-xs text-espresso/75"
              data-testid="editorial-v2-story-author-mode">
              {isFirstSave
                ? "Новая авторская статья. Текст и фото примера не копируются. Запись появится только после сохранения."
                : "Это образец оформления. Он сохраняется неизменным при создании новой статьи."}
            </div>
          )}

          {handoffConflict && (
            <div className="rounded-xl border border-espresso/15 bg-cream/70 px-4 py-3" data-testid="editorial-v2-story-handoff-conflict">
              <div className="text-sm text-espresso/80">В редакционной истории уже есть текст.</div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" onClick={dismissHandoffConflict}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-1.5 text-xs text-espresso"
                  data-testid="editorial-v2-story-handoff-keep">
                  Открыть существующий
                </button>
                <button type="button" onClick={applyHandoffReplace}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-1.5 text-xs text-espresso"
                  data-testid="editorial-v2-story-handoff-replace">
                  Заменить текст продолжением из обложки
                </button>
              </div>
            </div>
          )}

          {handoffNote && !handoffConflict && (
            <div className="text-xs text-espresso/60" data-testid="editorial-v2-story-handoff-note">
              Текст продолжен из обложки.
            </div>
          )}

          {/* Text input — shown before recognition, or when the author
              explicitly reopens it via "Изменить исходный текст". */}
          {(!recognized || sourceEditing) && (
            <div>
              <label className="mb-1 block text-[11px] text-espresso/65">
                {recognized ? "Исходный текст" : "Вставить текст"}
              </label>
              <textarea
                value={sourceText}
                onChange={(e) => handleSourceTextChange(e.target.value)}
                rows={10}
                placeholder={"Заголовок: ...\nПодзаголовок: ...\nВступление: ...\n\nОсновной текст материала..."}
                className="w-full rounded-xl border border-espresso/20 bg-white px-3 py-3 text-sm leading-relaxed outline-none focus:border-espresso/45"
                data-testid="editorial-v2-story-text"
              />
              {recognized && sourceEditing && (
                <button type="button" onClick={returnToRecognized}
                  className="mt-2 text-xs text-espresso/60 underline underline-offset-2"
                  data-testid="editorial-v2-story-return-to-recognized">
                  Вернуться к подготовленному тексту
                </button>
              )}
            </div>
          )}

          {/* Photo controls */}
          <div>
            {mediaUrl ? (
              <div className="flex items-start gap-3">
                <img src={displayMediaUrl || ""} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover"
                  style={{ objectPosition: `${desktopCrop.focal_x * 100}% ${desktopCrop.focal_y * 100}%` }} />
                <div className="flex flex-1 flex-wrap gap-2">
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50"
                    data-testid="editorial-v2-story-replace-photo">
                    {uploading ? "Загрузка…" : "Заменить фото"}
                  </button>
                  <button type="button" onClick={() => setLibraryOpen(true)}
                    className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
                    data-testid="editorial-v2-story-open-library">
                    Из Медиатеки
                  </button>
                  <button type="button" onClick={() => setCropOpen("desktop")}
                    className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
                    data-testid="editorial-v2-story-crop-desktop">
                    Настроить кадр
                  </button>
                  <button type="button" onClick={removePhoto}
                    className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso/70"
                    data-testid="editorial-v2-story-remove-photo">
                    Убрать фото
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50"
                  data-testid="editorial-v2-story-add-photo">
                  {uploading ? "Загрузка…" : "Добавить фотографию"}
                </button>
                <button type="button" onClick={() => setLibraryOpen(true)}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
                  data-testid="editorial-v2-story-open-library-empty">
                  Из Медиатеки
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelected}
              data-testid="editorial-v2-story-file-input"
            />

            {mediaUrl && (
              <div className="mt-3 rounded-xl border border-espresso/10 bg-white/60 px-3 py-3 text-sm text-espresso/70">
                <div className="font-medium text-espresso">Mobile</div>
                <div className="mt-0.5">Используется основное фото</div>
                <button type="button" onClick={() => setCropOpen("mobile")}
                  className="mt-2 rounded-lg border border-espresso/20 bg-white px-3 py-1.5 text-xs"
                  data-testid="editorial-v2-story-crop-mobile">
                  Настроить мобильный кадр
                </button>
              </div>
            )}
          </div>

          {/* Recognize action, only before a result exists */}
          {!recognized && (
            <div>
              <button type="button" onClick={recognize} disabled={!sourceText.trim()}
                className="rounded-xl bg-espresso px-5 py-2.5 text-sm text-cream disabled:opacity-40"
                data-testid="editorial-v2-story-recognize">
                Распознать текст
              </button>
              <p className="mt-2 text-xs text-espresso/60">
                Редакция сама подготовит структуру этого разворота.
              </p>
            </div>
          )}

          {/* Result after recognition */}
          {recognized && (
            <div className="rounded-2xl border border-espresso/15 bg-white/70 px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-espresso">
                  <span aria-hidden="true">✓</span>
                  <span>Текст подготовлен</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={() => setEditingFields((v) => !v)}
                    className="text-xs text-espresso/60 underline underline-offset-2"
                    data-testid="editorial-v2-story-edit-toggle">
                    {editingFields ? "Свернуть" : "Редактировать текст"}
                  </button>
                  <button type="button" onClick={() => setSourceEditing(true)}
                    className="text-xs text-espresso/60 underline underline-offset-2"
                    data-testid="editorial-v2-story-edit-source">
                    Изменить исходный текст
                  </button>
                </div>
              </div>

              {!editingFields ? (
                <div className="mt-3 space-y-1">
                  {recognized.headline && (
                    <div className="font-serif text-lg text-espresso">{recognized.headline}</div>
                  )}
                  {recognized.subtitle && (
                    <div className="text-sm text-espresso/70">{recognized.subtitle}</div>
                  )}
                  {recognized.lead && (
                    <div className="text-sm text-espresso/60">{recognized.lead}</div>
                  )}
                  {recognized.quote && (
                    <div className="text-sm italic text-espresso/60">«{recognized.quote}»</div>
                  )}
                  <div className="text-xs text-espresso/50">
                    {paragraphCount > 0
                      ? `Абзацев: ${paragraphCount}`
                      : "Основной текст пока пуст."}
                  </div>
                </div>
              ) : (
                <div className="mt-3 space-y-4">
                  <div className="grid gap-3">
                    <div>
                      <label className="mb-1 block text-[11px] text-espresso/60">Надзаголовок</label>
                      <input value={recognized.eyebrow} onChange={(e) => updateField("eyebrow", e.target.value)}
                        className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                        data-testid="editorial-v2-story-field-eyebrow" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] text-espresso/60">Заголовок</label>
                      <input value={recognized.headline} onChange={(e) => updateField("headline", e.target.value)}
                        className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                        data-testid="editorial-v2-story-field-headline" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] text-espresso/60">Подзаголовок</label>
                      <input value={recognized.subtitle} onChange={(e) => updateField("subtitle", e.target.value)}
                        className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                        data-testid="editorial-v2-story-field-subtitle" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] text-espresso/60">Вступление</label>
                      <textarea value={recognized.lead} onChange={(e) => updateField("lead", e.target.value)} rows={3}
                        className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                        data-testid="editorial-v2-story-field-lead" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] text-espresso/60">Цитата</label>
                      <textarea value={recognized.quote} onChange={(e) => updateField("quote", e.target.value)} rows={2}
                        className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                        data-testid="editorial-v2-story-field-quote" />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] text-espresso/60">Основной текст</label>
                    <div className="space-y-2">
                      {(recognized.paragraphs || []).map((p, i) => (
                        <div key={i} className="rounded-xl border border-transparent focus-within:border-espresso/15 focus-within:bg-cream/50 p-1">
                          <div className="flex gap-2">
                            <textarea value={p} onChange={(e) => { lastSelectionRef.current = null; updateParagraph(i, e.target.value); }} rows={3}
                              ref={(node) => { if (node) paragraphInputsRef.current[i] = node; else delete paragraphInputsRef.current[i]; }}
                              onFocus={() => setActiveParagraphIndex(i)}
                              onSelect={(e) => {
                                setActiveParagraphIndex(i);
                                lastSelectionRef.current = { index: i, start: e.currentTarget.selectionStart,
                                  end: e.currentTarget.selectionEnd, text: e.currentTarget.value };
                              }}
                              className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                              data-testid={`editorial-v2-story-paragraph-${i}`} />
                            <button type="button" onClick={() => removeParagraph(i)}
                              className="shrink-0 self-start rounded-lg border border-espresso/20 bg-white px-2 py-1 text-xs text-espresso/60"
                              data-testid={`editorial-v2-story-paragraph-remove-${i}`}>
                              Удалить
                            </button>
                          </div>
                          {activeParagraphIndex === i && (
                            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-cream/75 p-2"
                              data-testid="editorial-story-format-toolbar">
                              <span className="mr-1 text-xs text-espresso/60">Оформление абзаца {i + 1}</span>
                              {[
                                ["bold", "Ж", "Жирный — выделите слова"],
                                ["italic", "К", "Курсив — выделите слова"],
                                ["heading", "Подзаголовок", "Подзаголовок из этого абзаца"],
                                ["quote", "Цитата", "Цитата из этого абзаца"],
                              ].map(([kind, label, title]) => (
                                <button key={kind} type="button" title={title} aria-label={title}
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => formatActiveParagraph(kind, i)}
                                  className={"rounded-lg border border-espresso/20 bg-white px-3 py-1.5 text-xs text-espresso " + (kind === "bold" ? "font-bold" : kind === "italic" ? "italic" : "")}
                                  data-testid={"editorial-story-format-" + kind}>{label}</button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={addParagraph}
                      className="mt-2 text-xs text-espresso/60 underline underline-offset-2"
                      data-testid="editorial-v2-story-paragraph-add">
                      + Добавить абзац
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Local Story preview — Desktop A/B or Mobile stack, per the
              confirmed current EDITORIAL_STORY Reader contract. */}
          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4" data-testid="editorial-v2-story-preview">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "desktop" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-story-preview-desktop">
                    Desktop
                  </button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "mobile" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-story-preview-mobile">
                    Mobile
                  </button>
                </div>
              </div>

              {previewDevice === "desktop" ? (
                <div className="mx-auto max-w-lg" data-testid={`editorial-v2-story-preview-variant-${storyVariant}`}>
                  <div className="grid grid-cols-2 overflow-hidden rounded-2xl" style={{ backgroundColor: backgroundColor(storyBackground) }}>
                    {storyVariant === "B" ? (
                      <>
                        <div className="flex items-center p-5">
                          <StoryTextPreview recognized={recognized} textColor={textColorFor(storyBackground)}
                            accentColor={paletteColor(storyPalette)} backgroundHex={backgroundColor(storyBackground)} />
                        </div>
                        <StoryPreviewPhoto mediaUrl={displayMediaUrl} crop={desktopCrop} aspect={desktopAspect} rounded={false} />
                      </>
                    ) : (
                      <>
                        <StoryPreviewPhoto mediaUrl={displayMediaUrl} crop={desktopCrop} aspect={desktopAspect} rounded={false} />
                        <div className="flex items-center p-5">
                          <StoryTextPreview recognized={recognized} textColor={textColorFor(storyBackground)}
                            accentColor={paletteColor(storyPalette)} backgroundHex={backgroundColor(storyBackground)} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mx-auto max-w-[240px] overflow-hidden rounded-2xl text-center"
                  style={{ backgroundColor: backgroundColor(storyBackground) }}
                  data-testid="editorial-v2-story-preview-mobile-order">
                  <div className="px-3 pt-4">
                    {recognized?.eyebrow && (
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: paletteColor(storyPalette) }}>
                        {recognized.eyebrow}
                      </div>
                    )}
                    {recognized?.headline && (
                      <div className="mt-1 font-serif text-lg leading-snug" style={{ color: textColorFor(storyBackground) }}>
                        {typographicNbsp(recognized.headline)}
                      </div>
                    )}
                    {recognized?.subtitle && (
                      <div className="mt-2 font-serif text-sm italic opacity-90" style={{ color: textColorFor(storyBackground) }}>
                        {typographicNbsp(recognized.subtitle)}
                      </div>
                    )}
                    {recognized?.lead && (
                      <div className="mt-2 text-xs opacity-80" style={{ color: textColorFor(storyBackground) }}>
                        {typographicNbsp(recognized.lead)}
                      </div>
                    )}
                  </div>

                  <div className="mt-3">
                    <StoryPreviewPhoto mediaUrl={displayMediaUrl} crop={mobileCrop} aspect={mobileAspect} rounded={false} />
                  </div>

                  <div className="px-3 pb-4 pt-3 text-left">
                    {(() => {
                      const paragraphs = recognized?.paragraphs || [];
                      const shown = paragraphs.slice(0, 3);
                      const hasMore = paragraphs.length > 3;
                      if (!shown.length) return null;
                      return (
                        <div className="space-y-2">
                          {shown.map((p, i) => (
                            <EditorialStoryFormattedParagraphV1 key={i} text={p} preview
                              className="text-xs leading-relaxed opacity-75" style={{ color: textColorFor(storyBackground) }} />
                          ))}
                          {hasMore && (
                            <div className="text-[10px] italic opacity-60" style={{ color: textColorFor(storyBackground) }}>
                              Текст продолжается…
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {recognized?.quote && (
                      <div className="mt-3 border-l-2 pl-3 font-serif text-sm italic opacity-90"
                        style={{ color: textColorFor(storyBackground), borderColor: paletteColor(storyPalette) }}>
                        {typographicNbsp(recognized.quote)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {sourceNeedsRecognition && (
          <div className="border-t border-espresso/10 bg-cream/70 px-5 py-2 text-center text-xs text-espresso/60"
            data-testid="editorial-v2-story-save-guard">
            Сначала распознайте новый текст.
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          {!isShowcase && volume?.slug !== "open-library" && volume?.slug !== "open-library-author-intro" && <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />}
          {isShowcase && !isFirstSave && (
            <button type="button" onClick={onStartNewStory}
              className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
              data-testid="editorial-v2-story-start-new">
              Начать свою статью
            </button>
          )}
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-story-close">
            Закрыть
          </button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-story-preview-toggle">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          {(!isShowcase || isFirstSave) && (
            <button type="button" onClick={save}
              disabled={saveState === "saving" || sourceNeedsRecognition || (isFirstSave && !sourceText.trim())}
              className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50"
              data-testid="editorial-v2-story-save">
              {isFirstSave ? "Сохранить статью" : "Сохранить изменения"}
            </button>
          )}
        </div>
      </div>

      <MediaLibraryPickerV2 open={libraryOpen} onClose={() => setLibraryOpen(false)} onPick={pickFromLibrary} accessZone={mediaAccessZone} />

      <EditorialCropV2
        open={cropOpen === "desktop"}
        url={displayMediaUrl}
        aspectRatio={desktopAspect}
        frameLabel={desktopFrameLabel}
        focalX={desktopCrop.focal_x}
        focalY={desktopCrop.focal_y}
        zoom={desktopCrop.zoom}
        onSave={(values) => saveCrop("desktop", values)}
        onClose={() => setCropOpen(null)}
      />
      <EditorialCropV2
        open={cropOpen === "mobile"}
        url={displayMediaUrl}
        aspectRatio={mobileAspect}
        frameLabel={mobileFrameLabel}
        focalX={mobileCrop.focal_x}
        focalY={mobileCrop.focal_y}
        zoom={mobileCrop.zoom}
        onSave={(values) => saveCrop("mobile", values)}
        onClose={() => setCropOpen(null)}
      />
    </div>
  );
}
